/*
 * QuotePdfPreview.jsx — Iter 186 (Feb 2026)
 *
 * Faithful port of the Quote PDF template from /public/_mockup_ref.html.
 * Renders a US-Letter sized page with repeating navy letterhead + footer,
 * cover block (title + parties), pricing table, sum-table totals, terms
 * block, and signature block. Auto-paginates when content overflows a
 * page's body area by measuring scrollHeight after each render pass.
 *
 * Props:
 *   quoteNumber           string ("QT-1003")
 *   quoteTitle            string ("Quote")
 *   preparedFor / preparedBy  { org, name, addr, mail }
 *   lineItems             array (uses QuoteBuilder line-item shape)
 *   totals                { monthly, annual, once, rates } from pricing engine
 *   terms                 string
 *   proposalMode          boolean — when true, doc-sub reads "Proposal and quote"
 *   issuedAt, validUntil  Date | ISO string (defaults computed if omitted)
 *   onClose(), onDownload()   toolbar callbacks
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { totals as engineTotals } from "@/lib/quotePricingEngine";
import "./quotePdfPreview.css";
import { LOGO_WHITE } from "@/assets/enableHealthcareLogos";
import { renderQuoteRichText } from "@/components/quotes/QuoteRichTextField";

const fmtMoney = (n, dp = 0) => {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: dp, maximumFractionDigits: dp });
};

// Shows cents only when the amount actually has a fractional part, so clean
// totals stay "$750" while a $0.15 fee keeps "$750.15" accurate.
const fmtMoneyAuto = (n) => {
  const v = Number(n) || 0;
  return fmtMoney(v, Math.round(v * 100) % 100 === 0 ? 0 : 2);
};

const fmtDate = (d) => {
  try {
    const x = d instanceof Date ? d : new Date(d);
    return x.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
};

// Capture every rendered `.page` under `root` into a US-Letter PDF. Shared by
// Download and the email attachment so both produce the identical file.
async function pagesToPdf(root) {
  const pages = Array.from(root.querySelectorAll(".page"));
  if (!pages.length) throw new Error("No quote pages rendered");
  const pdf = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  for (let i = 0; i < pages.length; i++) {
    const canvas = await html2canvas(pages[i], { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
    if (i > 0) pdf.addPage("letter", "portrait");
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, pw, ph);
  }
  return pdf;
}

// ── Page shell — receives raw child blocks; letterhead + footer are
// re-emitted for every page created by the auto-pagination pass.
function PageShell({ pageNum, totalPages, quoteNumber, issuedAt, validUntil, preparedForOrg, children }) {
  return (
    <div className="page" data-testid={`qb-pdf-page-${pageNum}`}>
      <div className="p-band">
        {/* Iter 186.1 — real white PNG logo from mockup replaces the
            text placeholder. Fixed-height so cross-browser render is
            deterministic. */}
        <img
          src={LOGO_WHITE}
          alt="Enable Healthcare"
          className="p-logo-img"
          data-testid="qb-pdf-letterhead-logo"
        />
      </div>
      <div className="p-strip">
        <span>Quote <b>#{quoteNumber || "—"}</b> · Issued {fmtDate(issuedAt)} · Valid until {fmtDate(validUntil)}</span>
        <span>Prepared for <b>{preparedForOrg || "—"}</b></span>
      </div>
      <div className="p-body">{children}</div>
      <div className="p-foot">
        <span>
          <a href="https://www.ehiehr.com" target="_blank" rel="noreferrer">www.ehiehr.com</a>
          &nbsp;|&nbsp; 866 876 8272 &nbsp;|&nbsp; 225 Brae Blvd, Suite 125 Park Ridge, NJ 07656
        </span>
        <span className="pgno">Page {pageNum} of {totalPages}</span>
      </div>
    </div>
  );
}

// Build the ordered list of block elements to be paginated.
function buildBlocks({ quoteTitle, proposalMode, proposalSections, preparedFor, preparedBy, lineItems, ehiLineItems, totals, notes, terms }) {
  const blocks = [];

  // Cover block.
  blocks.push(
    <div className="blk" key="cover">
      <p className="doc-title">{quoteTitle || "Quote"}</p>
      <p className="doc-sub">{proposalMode ? "Proposal and quote" : "Quote"} — Enable Healthcare · Ariaone platform</p>
    </div>
  );

  // Parties.
  blocks.push(
    <div className="parties" key="parties">
      <div className="party">
        <label>Prepared for</label>
        <b>{preparedFor?.org || "—"}</b>
        {preparedFor?.name && <span>{preparedFor.name}</span>}
        {preparedFor?.addr && <span>{preparedFor.addr}</span>}
        {preparedFor?.mail && <span>{preparedFor.mail}</span>}
        {preparedFor?.phone && <span>{preparedFor.phone}</span>}
        {preparedFor?.clientId && <span data-testid="quote-preview-ehi-client-id">EHI Client ID: {preparedFor.clientId}</span>}
      </div>
      <div className="party">
        <label>Prepared by</label>
        <b>{preparedBy?.org || "Enable Healthcare Inc."}</b>
        {preparedBy?.name && <span>{preparedBy.name}</span>}
        {preparedBy?.addr && <span>{preparedBy.addr}</span>}
        {preparedBy?.mail && <span>{preparedBy.mail}</span>}
      </div>
    </div>
  );

  // Iter 188.53 · Rich-text proposal sections. Rendered when proposalMode
  // is on — they flow before the pricing table so the client reads the
  // narrative first ("Overview", "Why this matters", etc.) then the
  // numbers. Each section is one block for pagination purposes.
  if (proposalMode && Array.isArray(proposalSections)) {
    proposalSections.forEach((s, i) => {
      const title = (s?.title || "").trim();
      const html = (s?.html || "").trim();
      if (!title && !html) return;
      blocks.push(
        <div className="blk richbody" key={`prop-sec-${s?.id || i}`} data-testid={`qb-pdf-proposal-section-${i}`}>
          {title && <h2>{title}</h2>}
          {html && <div dangerouslySetInnerHTML={{ __html: html }} />}
        </div>
      );
    });
  }


  // Pricing block header.
  blocks.push(
    <div className="blk" key="pricing-intro">
      <h2>Quote</h2>
      <p>Fees are stated by billing basis and cadence. Monthly, annual and one-time charges are listed separately and are not combined. Percentage-based fees bill on actual payer reimbursement or collections and are stated as a rate.</p>
    </div>
  );

  // Pricing table (single block — long tables paginate cell-level in
  // reference, but we keep it whole for simplicity and let the whole
  // block flow to a new page if needed).
  // Mirror the downloaded PDF (services/quote_pdf.py) line-item logic so the
  // on-screen preview matches it. Earlier this read it.name / it.price — the
  // wrong fields — so Pro Bundle showed "—" / "$0". Line items carry
  // product / unit_price / total / basis. Section headings are excluded.
  const money2 = (n) => fmtMoney(n, 2);
  const rows = (lineItems || [])
    .filter((it) => !(it.kind === "heading" || it.isHeading || it.pricing_model === "heading"))
    .map((it, i) => {
      const alt = i % 2 ? "alt" : "";
      const name = it.product || it.name || it.product_name || "—";
      const qty = it.quantity ?? it.qty ?? "";
      const unit = Number(it.unit_price ?? it.price ?? it.rate ?? 0) || 0;
      let lineTotal = it.total;
      if (lineTotal === null || lineTotal === undefined || lineTotal === "") {
        lineTotal = unit * (Number(qty) || 1);
      }
      lineTotal = Number(lineTotal) || 0;
      const basis = (it.basis || "").toLowerCase();
      let rateCell, feeCell;
      if (basis === "pctcoll" || basis === "pctreimb") {
        const ofWhat = basis === "pctcoll" ? "collections" : "reimbursement";
        rateCell = `${unit}%`;
        feeCell = <span style={{ color: "#6D40C8" }}>{unit}% of {ofWhat}</span>;
      } else if (basis === "annual") {
        rateCell = `${money2(unit)}/yr`;
        feeCell = `${money2(lineTotal)}/yr`;
      } else if (basis === "onetime") {
        rateCell = money2(unit);
        feeCell = `${money2(lineTotal)} one-time`;
      } else {
        rateCell = money2(unit);
        feeCell = money2(lineTotal);
      }
      return (
        <tr key={it.id || i} className={alt} data-testid={`qb-pdf-pricing-row-${it.id || i}`}>
          <td>{name}</td>
          <td className="desc">{it.description || it.desc || ""}</td>
          <td className="c-num">{(qty !== "" && qty !== null && qty !== undefined) ? qty : "—"}</td>
          <td className="c-num">{rateCell}</td>
          <td className="c-num fee">{feeCell}</td>
        </tr>
      );
    });

  // EHI Care-Coordination Programs (CCM / RPM / APCM / AWV) added via the
  // top box are stored separately (ehi_computed.line_items) and were missing
  // from this preview. Render them into the same pricing table so the
  // on-screen preview matches the downloaded PDF. Percentage programs state
  // the rate (no dollar); fixed / per-unit programs show the amount.
  const ehiRows = (ehiLineItems || []).map((li, i) => {
    const prog = (li.program || "").trim();
    let label = (li.label || prog).trim();
    if (prog && label.toUpperCase().startsWith(prog.toUpperCase())) {
      label = label.slice(prog.length).replace(/^[\s—–-]+/, "").trim() || (li.label || "");
    }
    const note = (li.note || "").trim();
    const qty = li.qty;
    const rate = li.rate;
    const oneTime = Number(li.one_time) || 0;
    const monthly = Number(li.monthly) || 0;
    const annual = Number(li.annual) || 0;
    const isPct = rate == null && qty == null && oneTime === 0 && monthly === 0 && annual === 0;
    let fee;
    if (isPct) fee = <span style={{ color: "#6D40C8" }}>{note || "Percentage of payer reimbursement"}</span>;
    else if (oneTime > 0) fee = `${fmtMoney(oneTime)} one-time`;
    else if (monthly > 0) fee = `${fmtMoney(monthly)}/mo`;
    else if (annual > 0) fee = `${fmtMoney(annual)}/yr`;
    else fee = "—";
    const alt = (rows.length + i) % 2 ? "alt" : "";
    return (
      <tr key={`ehi-${i}`} className={alt} data-testid={`qb-pdf-ehi-row-${i}`}>
        <td>{prog || "Program"}</td>
        <td className="desc">{label}{!isPct && note ? <div className="mut" style={{ fontSize: "9px" }}>{note}</div> : null}</td>
        <td className="c-num">{(qty !== null && qty !== undefined && qty !== "") ? qty : "—"}</td>
        <td className="c-num">{rate != null ? fmtMoney(rate, 2) : "—"}</td>
        <td className="c-num fee">{fee}</td>
      </tr>
    );
  });

  blocks.push(
    <div className="blk" key="pricing-table">
      <table className="pdf" data-testid="qb-pdf-pricing-table">
        <colgroup>
          <col style={{ width: "26%" }} />
          <col />
          <col style={{ width: "12%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "18%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>Item</th><th>Description</th>
            <th className="c-num">Qty / vol</th>
            <th className="c-num">Rate</th>
            <th className="c-num">Fee</th>
          </tr>
        </thead>
        <tbody>
          {(rows.length || ehiRows.length) ? [...rows, ...ehiRows] : (
            <tr><td colSpan={5} className="mut">No line items yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // Sum-table.
  const t = totals || {};
  blocks.push(
    <div className="blk" key="sumtable">
      <table className="sumtable" data-testid="qb-pdf-sumtable">
        <tbody>
          {t.fixed ? <tr><td>Fixed monthly</td><td className="v">{fmtMoneyAuto(t.fixed)}/mo</td></tr> : null}
          {t.variable ? <tr><td>Volume-based (estimated)</td><td className="v">{fmtMoneyAuto(t.variable)}/mo</td></tr> : null}
          <tr className="tot"><td>Estimated monthly</td><td className="v">{fmtMoneyAuto(t.monthly_recurring || t.monthly || 0)}/mo</td></tr>
          {t.annual ? <tr><td>Billed annually</td><td className="v">{fmtMoneyAuto(t.annual)}/yr</td></tr> : null}
          {(t.one_time || t.once) ? <tr><td>One-time charges</td><td className="v">{fmtMoneyAuto(t.one_time || t.once)}</td></tr> : null}
        </tbody>
      </table>
      <p className="fineprint">Monthly, annual and one-time charges are stated separately and are not additive. Estimated monthly excludes percentage-based fees; EHI retains a percentage of what the payer reimburses or the practice collects, and because every payer reimburses differently these cannot be quoted as a dollar amount in advance. Volume-based estimates use quantities supplied by the Client and bill on actual counts.</p>
    </div>
  );

  // Notes block (client-visible). Placed directly ABOVE Terms, same styling.
  // Internal Notes are intentionally NOT rendered here — they stay internal.
  if (notes && String(notes).trim()) {
    blocks.push(
      <div className="blk" key="notes" data-testid="qb-pdf-notes">
        <h2>Notes</h2>
        {renderQuoteRichText(notes)}
      </div>
    );
  }

  // Terms block. Renders stored rich-text HTML (with list/heading markers);
  // legacy plain text keeps its line breaks.
  if (terms && String(terms).trim()) {
    blocks.push(
      <div className="blk" key="terms">
        <h2>Terms and Conditions</h2>
        {renderQuoteRichText(terms)}
      </div>
    );
  }

  // Signature block — always last.
  blocks.push(
    <div className="blk sigs" key="sigs" data-testid="qb-pdf-signatures">
      <div className="sig">
        <label>Accepted &amp; agreed — Practice</label>
        <div className="ln" /><div className="cap">Authorised signer</div>
        <div className="ln" style={{ marginTop: 18 }} /><div className="cap">Title</div>
        <div className="ln" style={{ marginTop: 18 }} /><div className="cap">Date</div>
      </div>
      <div className="sig">
        <label>Enable Healthcare</label>
        <div className="ln" /><div className="cap">Enable Healthcare</div>
        <div className="ln" style={{ marginTop: 18 }} /><div className="cap">Title</div>
        <div className="ln" style={{ marginTop: 18 }} /><div className="cap">Date</div>
      </div>
    </div>
  );

  return blocks;
}

export default function QuotePdfPreview({
  quoteNumber, quoteTitle, proposalMode, proposalSections,
  preparedFor, preparedBy, lineItems, ehiLineItems, totals, notes, terms,
  issuedAt, validUntil, onClose, onDownload, inline = false, onReady,
}) {
  // Default dates: today, +30 days.
  const iso = useMemo(() => issuedAt || new Date(), [issuedAt]);
  const vu = useMemo(() => validUntil || new Date(Date.now() + 30 * 86400 * 1000), [validUntil]);

  const blocks = useMemo(
    () => buildBlocks({ quoteTitle, proposalMode, proposalSections, preparedFor, preparedBy, lineItems, ehiLineItems, totals, notes, terms }),
    [quoteTitle, proposalMode, proposalSections, preparedFor, preparedBy, lineItems, ehiLineItems, totals, notes, terms]
  );

  // Auto-pagination: after mount, measure each page's `.p-body` and
  // move overflowing blocks to a new page. Two-pass so page numbers
  // land correctly in the footer.
  const measureRootRef = useRef(null);
  const [pageAssignments, setPageAssignments] = useState(() => [blocks.map((_, i) => i)]);

  useEffect(() => {
    if (!measureRootRef.current) return;
    const bodies = measureRootRef.current.querySelectorAll(".p-body");
    let overflowFound = false;
    const newPages = pageAssignments.map((p) => [...p]);
    bodies.forEach((body, pageIdx) => {
      if (body.scrollHeight > body.clientHeight + 2 && newPages[pageIdx].length > 1) {
        // Pop last block, push to a new page.
        const moved = newPages[pageIdx].pop();
        if (!newPages[pageIdx + 1]) newPages.push([]);
        newPages[pageIdx + 1].unshift(moved);
        overflowFound = true;
      }
    });
    if (overflowFound) setPageAssignments(newPages);
  }, [pageAssignments, blocks.length]);

  // Re-derive when blocks change.
  useEffect(() => {
    setPageAssignments([blocks.map((_, i) => i)]);
  }, [blocks]);

  const totalPages = pageAssignments.length;

  // Direct download: capture the exact on-screen preview pages and write them
  // into a US-Letter PDF (each .page is already Letter-proportioned 816×1056 =
  // 612×792pt), so the downloaded file is pixel-faithful to the preview. No
  // print dialog.
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    const root = measureRootRef.current;
    if (!root || downloading) return;
    setDownloading(true);
    try {
      const pdf = await pagesToPdf(root);
      pdf.save(`Quote_${quoteNumber || "preview"}.pdf`);
    } catch (e) {
      console.error("Quote PDF download failed, falling back to print", e);
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  // Hand the download fn up to a parent (e.g. the quote detail page) so its
  // own "Download" button produces the exact same file as this preview.
  useEffect(() => {
    if (onReady) onReady({ download: handleDownload });
  });

  const pagesEl = (
    <div className="qb-pdf-root" ref={measureRootRef}>
      {pageAssignments.map((blockIdxs, pageIdx) => (
        <PageShell
          key={`page-${pageIdx}`}
          pageNum={pageIdx + 1}
          totalPages={totalPages}
          quoteNumber={quoteNumber}
          issuedAt={iso}
          validUntil={vu}
          preparedForOrg={preparedFor?.org}
        >
          {blockIdxs.map((i) => <React.Fragment key={i}>{blocks[i]}</React.Fragment>)}
        </PageShell>
      ))}
    </div>
  );

  // Inline mode (quote detail page): render the pages directly in-flow, no
  // dark overlay / toolbar. The parent supplies its own Download button wired
  // through onReady so one document is used everywhere.
  if (inline) {
    return (
      <div className="qb-pdf-inline" data-testid="qb-pdf-inline">
        {pagesEl}
      </div>
    );
  }

  return (
    <div className="qb-pdf-overlay" data-testid="qb-pdf-preview-overlay">
      <div className="qb-pdf-toolbar">
        <button onClick={onClose} data-testid="qb-pdf-preview-close">← Back to builder</button>
        <div className="spacer" />
        <button className="primary" onClick={handleDownload} disabled={downloading} data-testid="qb-pdf-download">
          {downloading ? "Preparing…" : "Download"}
        </button>
      </div>
      {pagesEl}
    </div>
  );
}

// ── Shared mapper ──────────────────────────────────────────────────────────
// Derives the full QuotePdfPreview prop-set from a SAVED quote document, using
// the same pricing-engine totals the QuoteBuilder uses live, so the quote
// detail page (and any other surface) renders the EXACT same document as the
// edit-screen preview. Zero drift: totals are a pure function of line_items.
export function quotePreviewPropsFromQuote(quote, user) {
  const q = quote || {};
  const li = q.line_items || [];
  const buckets = engineTotals(li);
  const trueMRR = (buckets.fixedMonthly || 0) + (buckets.volumeMonthly || 0);
  const ehi = q.ehi_computed || {};
  return {
    quoteNumber: q.quote_number || "QT-XXXX",
    quoteTitle: q.title || "Quote",
    proposalMode: !!q.proposal_included,
    proposalSections: q.proposal_sections || [],
    preparedFor: {
      org: q.contact_company || "",
      name: q.contact_name || "",
      addr: q.contact_address || "",
      mail: q.contact_email || "",
      phone: q.recipient_type === "ehi_client" ? (q.contact_phone || "") : "",
      clientId: q.ehi_client_id || "",
    },
    preparedBy: {
      org: "Enable Healthcare Inc.",
      name: q.owner_name || q.sender_name || q.created_by_name || user?.full_name || user?.email || "",
      addr: "225 Brae Blvd, Suite 125 Park Ridge, NJ 07656",
      mail: q.owner_email || user?.email || "",
    },
    lineItems: li,
    ehiLineItems: ehi.line_items || [],
    totals: {
      fixed: buckets.fixedMonthly || 0,
      variable: (buckets.volumeMonthly || 0) + (ehi.totals?.monthly_recurring || 0),
      monthly_recurring: trueMRR + (ehi.totals?.monthly_recurring || 0),
      annual: buckets.annual || 0,
      one_time: (buckets.oneTime || 0) + (ehi.totals?.one_time || 0),
    },
    terms: q.terms || "",
    notes: q.notes || "",
    issuedAt: q.created_at || null,
    validUntil: q.valid_until || null,
  };
}

// Wait until auto-pagination stops moving blocks and images have loaded.
async function waitForStablePages(host, timeoutMs = 15000) {
  const start = Date.now();
  let last = -1;
  let stable = 0;
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 120));
    const n = host.querySelectorAll(".page").length;
    const imgsReady = Array.from(host.querySelectorAll("img")).every((im) => im.complete);
    stable = n > 0 && n === last && imgsReady ? stable + 1 : 0;
    last = n;
    if (stable >= 4) return;
  }
  throw new Error("Quote preview did not finish rendering");
}

// Render a saved quote off-screen with the exact Preview/Download document and
// return the PDF bytes (same renderer, same quality as Download).
export async function renderQuotePdfBytes(quote, user) {
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText = "position:fixed;left:-20000px;top:0;width:900px;pointer-events:none;";
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    root.render(<QuotePdfPreview inline {...quotePreviewPropsFromQuote(quote, user)} />);
    if (document.fonts?.ready) await document.fonts.ready;
    await waitForStablePages(host);
    const pdf = await pagesToPdf(host);
    return new Uint8Array(pdf.output("arraybuffer"));
  } finally {
    root.unmount();
    host.remove();
  }
}
